import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getBackendUrl } from '../utils/backendUrl';
import { ALL_COUNTRIES } from '../utils/postalCodeValidation';
import { trackEvent } from '../utils/telemetry';

const OCCASION_CATEGORIES = [
  { id: 'all', label: 'All', icon: '✨' },
  { id: 'kids', label: 'Kids Birthday', icon: '🎈' },
  { id: 'adult_bday', label: 'Adult Birthday', icon: '🍸' },
  { id: 'baby_shower', label: 'Baby Shower', icon: '🍼' },
  { id: 'dinner', label: 'Dinner Party', icon: '🍷' },
  { id: 'anniversary', label: 'Anniversary', icon: '🥂' },
  { id: 'graduation', label: 'Grad & Milestone', icon: '🎓' }
];

const INSPIRATIONS_BY_OCCASION = {
  all: [
    [
      { label: 'Woodland 4th ($800)', prompt: 'Enchanted woodland 4th birthday party for 20 kids in our backyard with low-mess activities, nut-free snacks, budget $800' },
      { label: '50th Golden ($2,500)', prompt: 'Golden 50th wedding anniversary dinner for 30 family members, elegant seated Italian dining, budget $2,500' },
      { label: 'Baby Shower Brunch ($1,000)', prompt: 'Pastel garden baby shower for 25 guests, sunroom brunch & floral mocktails, budget $1,000' },
      { label: 'Cocktail Mixer ($1,200)', prompt: 'Rooftop sunset cocktail mixer for 25 friends, craft mocktails & charcuterie, budget $1,200' }
    ],
    [
      { label: 'Dino Safari ($750)', prompt: 'Jurassic dinosaur expedition 5th birthday for 18 kids, backyard fossil dig activities, pizza & cupcakes, budget $750' },
      { label: 'Tuscan Dinner ($900)', prompt: 'Intimate candlelit Tuscan garden dinner for 14 close friends, handmade pasta, rustic antipasti, budget $900' },
      { label: 'Retro 80s Neon ($1,100)', prompt: 'Retro 80s arcade & neon bowling night for 20 adults, pizza slices, craft beers, nostalgic soundtrack, budget $1,100' },
      { label: 'Cloud Nine Shower ($850)', prompt: 'Whimsical Cloud Nine baby shower for 20 friends, celestial pastel florals, warm high tea sweets, budget $850' }
    ]
  ],
  kids: [
    [
      { label: 'Dino Safari ($750)', prompt: 'Jurassic dinosaur safari 5th birthday for 20 kids in our backyard, fossil dig stations, mini slider burgers & dirt cup desserts, budget $750' },
      { label: 'Space Galaxy ($850)', prompt: 'Outer space cosmic galaxy 7th birthday for 16 kids, DIY rocket building craft, stellar dessert table with planet cake pops, budget $850' },
      { label: 'Enchanted Storybook ($650)', prompt: 'Fairytale enchanted forest 4th birthday for 15 kids, fairy wing dress-up, magical berry fruit cups, budget $650' },
      { label: 'Superhero Academy ($800)', prompt: 'Action-packed superhero training obstacle course 6th birthday for 20 kids, mask making craft, budget $800' }
    ],
    [
      { label: 'Under the Sea ($700)', prompt: 'Whimsical mermaid and ocean reef 5th birthday for 18 kids, bubble play, seashell cookie decorating, budget $700' },
      { label: 'Junior Chefs ($900)', prompt: 'Interactive pizza making & cupcake bakery birthday for 12 kids, custom aprons, chef hats, budget $900' },
      { label: 'Safari Explorer ($800)', prompt: 'Wild safari jungle adventure 3rd birthday for 22 kids, animal mask craft, trail mix bar, budget $800' },
      { label: 'Science Volcano Lab ($650)', prompt: 'Mad scientist volcano & slime laboratory 8th birthday for 15 kids, glow-in-the-dark punch, budget $650' }
    ]
  ],
  adult_bday: [
    [
      { label: 'Disco Glam ($1,400)', prompt: 'Studio 54 neon disco 30th birthday lounge for 30 friends, signature glitter cocktails, mirror ball photo corner, budget $1,400' },
      { label: 'Palm Springs Retro ($1,200)', prompt: 'Mid-century Palm Springs pool & patio soirée for 20 guests, retro cocktails, charcuterie grazing board, budget $1,200' },
      { label: 'Speakeasy Velvet ($1,600)', prompt: 'Moody 1920s velvet & jazz speakeasy 40th birthday for 25 guests, craft bourbon tasting, passed hors d\'oeuvres, budget $1,600' },
      { label: 'Tacos & Tequila ($850)', prompt: 'Vibrant backyard taco fiesta with street corn, churro bar, fresh margarita flight for 25 friends, budget $850' }
    ],
    [
      { label: 'Moody Rooftop Wine ($1,300)', prompt: 'Golden hour rooftop wine & tapas celebration for 20 friends, string lighting, ambient chill lounge music, budget $1,300' },
      { label: 'VIP Lounge Karaoke ($750)', prompt: 'VIP lounge karaoke birthday with gourmet sliders, sparkling cocktail bar for 16 guests, budget $750' },
      { label: 'Garden Biergarten ($950)', prompt: 'Bavarian style craft beer garden with giant pretzels, gourmet sausages, live acoustic tunes for 30 guests, budget $950' },
      { label: 'Modern Izakaya ($1,500)', prompt: 'Sleek Japanese izakaya small plates, skewers, highballs, minimalist lantern styling for 18 friends, budget $1,500' }
    ]
  ],
  baby_shower: [
    [
      { label: 'Pastel Garden ($1,000)', prompt: 'Pastel botanical garden baby shower for 25 guests, sunroom brunch, floral mocktail spritz bar, budget $1,000' },
      { label: 'Cloud Nine High Tea ($850)', prompt: 'Whimsical Cloud Nine celestial baby shower, three-tier sweet & savory high tea stands, delicate macarons, budget $850' },
      { label: 'Teddy Bear Picnic ($750)', prompt: 'Storybook teddy bear honey harvest baby shower for 20 family & friends, rustic wooden tables, warm pastries, budget $750' },
      { label: 'Neutral Boho Chic ($1,100)', prompt: 'Warm neutral boho dried-floral baby shower, rattan seating, mimosa & mocktail station, grazing table, budget $1,100' }
    ],
    [
      { label: 'Citrus Sunshine ($900)', prompt: 'Little Cutie vibrant citrus sunshine baby shower for 24 guests, sparkling lemonade bar, fresh berry tarts, budget $900' },
      { label: 'Wildflower Meadow ($800)', prompt: 'Woodland wildflower garden celebration, watercolor decor, herbal tea and finger sandwiches for 20 guests, budget $800' },
      { label: 'Parisian Patisserie ($1,200)', prompt: 'French bakery morning shower with fresh croissants, espresso cart, blush carnations for 25 guests, budget $1,200' },
      { label: 'Twinkle Night Shower ($950)', prompt: 'Evening twinkle star and moon baby shower, dessert buffet, hot cocoa bar, cozy candlelight for 22 guests, budget $950' }
    ]
  ],
  dinner: [
    [
      { label: 'Tuscan Vineyard ($900)', prompt: 'Intimate candlelit Tuscan garden dinner for 14 close friends, handmade fresh pasta, rustic antipasti, budget $900' },
      { label: 'Moody Bistro ($1,100)', prompt: 'Warm Parisian bistro dinner party for 12 guests, steak frites, dark floral table runner, jazz playlist, budget $1,100' },
      { label: 'Spanish Tapas ($850)', prompt: 'Lively Spanish tapas & sangria night for 16 friends, gambas al ajillo, jamón ibérico, warm churros, budget $850' },
      { label: 'Farm-to-Table ($1,000)', prompt: 'Autumn farm-to-table harvest dinner for 16 guests, roasted heirloom vegetables, artisan sourdough, budget $1,000' }
    ]
  ],
  anniversary: [
    [
      { label: '50th Golden Gala ($2,500)', prompt: 'Golden 50th wedding anniversary dinner for 30 family members, elegant seated Italian dining, live acoustic violin, budget $2,500' },
      { label: 'Silver 25th Sunset ($1,800)', prompt: 'Seaside sunset 25th anniversary celebration for 20 close friends, champagne toast, fresh seafood platter, budget $1,800' },
      { label: 'Vintage 10th Romance ($1,200)', prompt: 'Intimate candlelit garden anniversary dinner for 15 guests, French champagne, tiered celebration cake, budget $1,200' },
      { label: 'Starlit Greenhouse ($1,500)', prompt: 'Romantic botanical greenhouse celebration for 25 guests, ambient fairy lights, artisan cocktails & dessert table, budget $1,500' }
    ]
  ],
  graduation: [
    [
      { label: 'Next Chapter Gala ($1,500)', prompt: 'College graduation celebration for 35 guests, black & gold modern styling, slider bar, photo memory wall, budget $1,500' },
      { label: 'Sunset Lawn Party ($900)', prompt: 'High school graduation backyard lawn party for 30 guests, lawn games, gourmet taco truck style buffet, budget $900' },
      { label: 'Winter Holiday Glow ($1,200)', prompt: 'Festive holiday gathering for 25 friends, mulled cider bar, pine & eucalyptus garland, warm hors d\'oeuvres, budget $1,200' },
      { label: 'Tropical Summer Bash ($1,000)', prompt: 'Vibrant tropical summer kickoff with fresh fruit skewers, island mocktails, upbeat summer lounge tunes for 30 guests, budget $1,000' }
    ]
  ]
};

const ExpressPlanModal = ({
  isOpen,
  onClose,
  onApplyPlan,
  onGenerateDirectly,
  initialCountry = 'US',
  initialLocation = ''
}) => {
  const [selectedOccasion, setSelectedOccasion] = useState('all');
  const [poolIndex, setPoolIndex] = useState(0);
  const [prompt, setPrompt] = useState(() => {
    try {
      return localStorage.getItem('cmp_fast_track_prompt') || '';
    } catch (e) {
      return '';
    }
  });
  const [restoredFromDraft, setRestoredFromDraft] = useState(() => {
    try {
      return !!localStorage.getItem('cmp_fast_track_prompt');
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    try {
      if (prompt && prompt.trim()) {
        localStorage.setItem('cmp_fast_track_prompt', prompt);
      } else {
        localStorage.removeItem('cmp_fast_track_prompt');
        setRestoredFromDraft(false);
      }
    } catch (e) {}
  }, [prompt]);

  const handleClearDraft = () => {
    setPrompt('');
    setRestoredFromDraft(false);
    try {
      localStorage.removeItem('cmp_fast_track_prompt');
    } catch (e) {}
  };
  const [country, setCountry] = useState(initialCountry || 'US');
  const [locationInput, setLocationInput] = useState(initialLocation || '');
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const fileInputRef = useRef(null);

  if (!isOpen || typeof document === 'undefined') return null;

  const currentPools = INSPIRATIONS_BY_OCCASION[selectedOccasion] || INSPIRATIONS_BY_OCCASION.all;
  const currentPills = currentPools[poolIndex % currentPools.length] || currentPools[0];

  const handleShufflePills = () => {
    setPoolIndex((prev) => prev + 1);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setParseError('Please upload a valid image file (JPG, PNG, WebP).');
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      setParseError('Image size must be under 8MB.');
      return;
    }

    setParseError(null);
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSurpriseMe = () => {
    // Pick randomly from the active occasion's inspiration pools
    const allIdeasForOccasion = currentPools.flatMap((p) => p.map((item) => item.prompt));
    const randomIdea = allIdeasForOccasion[Math.floor(Math.random() * allIdeasForOccasion.length)];
    setPrompt(randomIdea);
    setParseError(null);
  };

  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      setParseError('Geolocation is not supported by your browser.');
      return;
    }
    setIsDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`);
          if (res.ok) {
            const data = await res.json();
            const addr = data.address || {};
            const postcode = addr.postcode || '';
            const city = addr.city || addr.town || addr.village || '';
            const detectedCountry = (addr.country_code || 'us').toUpperCase();
            
            if (postcode || city) {
              setLocationInput(postcode || city);
            }
            if (detectedCountry) {
              setCountry(detectedCountry);
            }
          }
        } catch (e) {
          console.warn('Could not reverse geocode location:', e);
        } finally {
          setIsDetectingLocation(false);
        }
      },
      (err) => {
        console.warn('Geolocation permission denied or timed out:', err);
        setIsDetectingLocation(false);
      },
      { timeout: 8000 }
    );
  };

  const handleParse = async () => {
    if (!prompt.trim() && !imagePreview) {
      setParseError('Please describe your event vision or upload an inspiration photo.');
      return;
    }

    setIsParsing(true);
    setParseError(null);

    try {
      const payload = {
        prompt: prompt.trim() || undefined,
        image_base64: imagePreview || undefined,
        image_mime_type: imageFile?.type || 'image/jpeg',
        country: country,
        zip_code: locationInput.trim() || undefined
      };

      trackEvent('fast_track_prompt_submitted', {
        occasion: selectedOccasion,
        has_image: Boolean(imagePreview),
        prompt_length: prompt.length
      });

      const response = await fetch(`${getBackendUrl()}/api/express-plan-parse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Server returned status ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        // Guarantee user's chosen location and country are merged into parsed data
        const merged = {
          ...result.data,
          country: country || result.data.country || 'US',
          zip_code: locationInput.trim() || result.data.zip_code || ''
        };
        setExtractedData(merged);
      } else {
        throw new Error('Could not parse plan details.');
      }
    } catch (err) {
      console.error('Express parsing error:', err);
      setParseError('Unable to analyze vision right now. Please try again or continue with the standard wizard.');
    } finally {
      setIsParsing(false);
    }
  };

  const handleApply = () => {
    if (extractedData) {
      try { localStorage.removeItem('cmp_fast_track_prompt'); } catch (e) {}
      const finalData = {
        ...extractedData,
        country: country || extractedData.country || 'US',
        zip_code: locationInput.trim() || extractedData.zip_code || ''
      };
      trackEvent('fast_track_plan_applied', {
        occasion: finalData.occasion || selectedOccasion,
        direct_launch: false
      });
      onApplyPlan(finalData);
      onClose();
    }
  };

  const handleDirectLaunch = () => {
    if (extractedData) {
      try { localStorage.removeItem('cmp_fast_track_prompt'); } catch (e) {}
      const finalData = {
        ...extractedData,
        country: country || extractedData.country || 'US',
        zip_code: locationInput.trim() || extractedData.zip_code || ''
      };
      trackEvent('fast_track_plan_applied', {
        occasion: finalData.occasion || selectedOccasion,
        direct_launch: true
      });
      onGenerateDirectly(finalData);
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-stone-950/60 p-3 sm:p-6 backdrop-blur-sm animate-fade-in font-sans"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-[#FDFBF7] shadow-2xl border border-stone-200 p-5 sm:p-8 text-left"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-stone-100 text-stone-500 hover:bg-stone-200 hover:text-stone-800 transition"
          aria-label="Close modal"
        >
          ✕
        </button>

        {/* Modal Header */}
        <div className="mb-4 pr-8">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-stone-500">Fast-Track Setup</span>
          </div>
          <h3 className="text-xl sm:text-2xl font-black text-stone-900 tracking-tight">
            Describe Your Vision
          </h3>
          <p className="mt-1 text-xs sm:text-sm text-stone-600 leading-relaxed">
            Tell us about your event in plain language, or upload an invitation/Pinterest inspiration. We’ll extract the key details and build your plan.
          </p>
        </div>

        {!extractedData ? (
          <div className="space-y-4">
            {/* Occasion Filter Pills */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1.5">
                Target Occasion <span className="text-stone-400 font-normal">(filters ideas & prompts)</span>
              </label>
              <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar">
                {OCCASION_CATEGORIES.map((occ) => {
                  const isActive = selectedOccasion === occ.id;
                  return (
                    <button
                      key={occ.id}
                      type="button"
                      onClick={() => {
                        setSelectedOccasion(occ.id);
                        setPoolIndex(0);
                      }}
                      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition ${
                        isActive
                          ? 'bg-stone-900 text-white shadow-xs'
                          : 'bg-stone-100 hover:bg-stone-200 text-stone-700'
                      }`}
                    >
                      <span>{occ.icon}</span>
                      <span>{occ.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Prompt Textarea */}
            <div>
              {restoredFromDraft && (
                <div className="flex items-center justify-between text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg mb-2">
                  <span>✨ Restored from your previous draft</span>
                  <button
                    type="button"
                    onClick={handleClearDraft}
                    className="text-stone-500 hover:text-stone-800 underline font-bold"
                  >
                    Start fresh
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-bold text-stone-700">
                  Event Description & Preferences
                </label>
                <button
                  type="button"
                  onClick={handleSurpriseMe}
                  className="text-xs font-bold text-stone-600 hover:text-stone-900 hover:underline flex items-center gap-1 transition active:scale-95"
                  title="Generate a vision tailored to the selected occasion"
                >
                  <span>🎲</span> Example Idea
                </button>
              </div>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                placeholder="e.g., 5th birthday for 20 kids in our backyard next Saturday. Looking for a dinosaur adventure theme with pizza, low-mess crafts, and an $800 budget..."
                className="w-full p-4 bg-white border border-stone-300 rounded-xl text-sm font-medium text-stone-900 placeholder-stone-400 focus:border-stone-800 focus:outline-none transition resize-none leading-relaxed shadow-2xs"
              />
            </div>

            {/* Quick Inspiration Pills with Interactive Refresh */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] font-semibold text-stone-500 mr-1">Try:</span>
              {currentPills.map((pill, idx) => (
                <button
                  key={`${pill.label}-${idx}`}
                  type="button"
                  onClick={() => setPrompt(pill.prompt)}
                  className="text-[11px] font-medium px-2.5 py-1 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700 transition active:scale-95 text-left"
                >
                  {pill.label}
                </button>
              ))}
              <button
                type="button"
                onClick={handleShufflePills}
                className="text-[11px] font-bold px-2 py-1 rounded-lg bg-stone-200/80 hover:bg-stone-300 text-stone-800 transition flex items-center gap-1 active:scale-95"
                title="Shuffle alternative ideas"
              >
                <span>🔄</span> Shuffle
              </button>
            </div>

            {/* Optional Location & Country Row */}
            <div className="p-3 bg-stone-100/60 rounded-xl border border-stone-200/80">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-bold text-stone-700 flex items-center gap-1.5">
                  <span>📍</span> Location & Postal Code <span className="text-stone-400 font-normal">(for local shopping & vendors)</span>
                </label>
                <button
                  type="button"
                  onClick={handleDetectLocation}
                  disabled={isDetectingLocation}
                  className="text-[11px] font-bold text-stone-600 hover:text-stone-900 hover:underline flex items-center gap-1 disabled:opacity-50"
                >
                  {isDetectingLocation ? 'Detecting...' : '🎯 Auto-Detect'}
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-1">
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full h-9 px-2.5 bg-white border border-stone-300 rounded-lg text-xs font-semibold text-stone-800 focus:outline-none focus:border-stone-800"
                  >
                    {ALL_COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.code} - {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <input
                    type="text"
                    value={locationInput}
                    onChange={(e) => setLocationInput(e.target.value)}
                    placeholder={country === 'US' ? 'ZIP code (e.g. 90210)' : 'Postal code or city (e.g. SW1A 1AA, Toronto)'}
                    className="w-full h-9 px-3 bg-white border border-stone-300 rounded-lg text-xs font-semibold text-stone-800 placeholder-stone-400 focus:outline-none focus:border-stone-800"
                  />
                </div>
              </div>
            </div>

            {/* Inspiration Photo Dropzone */}
            <div>
              <label className="block text-xs font-bold text-stone-700 mb-1.5">
                Inspiration Image <span className="text-stone-400 font-normal">(optional)</span>
              </label>
              {imagePreview ? (
                <div className="relative inline-block rounded-xl border border-stone-300 p-1.5 bg-white shadow-2xs">
                  <img
                    src={imagePreview}
                    alt="Inspiration preview"
                    className="h-24 w-auto rounded-lg object-cover"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-stone-900 text-white rounded-full flex items-center justify-center text-xs font-bold shadow hover:bg-stone-700 transition"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border border-dashed border-stone-300 rounded-xl p-3.5 text-center cursor-pointer hover:border-stone-500 hover:bg-white transition flex items-center justify-center gap-3 group bg-stone-50/50"
                >
                  <span className="text-xl">📷</span>
                  <div className="text-left">
                    <p className="text-xs font-semibold text-stone-800">Add an invite card, Pinterest screenshot, or venue photo</p>
                    <p className="text-[11px] text-stone-400">JPG, PNG, or WebP up to 8MB</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </div>
              )}
            </div>

            {parseError && (
              <p className="text-xs font-semibold text-red-700 bg-red-50 p-3 rounded-xl border border-red-200">
                {parseError}
              </p>
            )}

            {/* Action Buttons */}
            <div className="pt-3 flex items-center justify-end gap-3 border-t border-stone-200/80">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-xs sm:text-sm font-semibold text-stone-600 hover:text-stone-900 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleParse}
                disabled={isParsing || (!prompt.trim() && !imagePreview)}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-stone-900 hover:bg-stone-800 text-white text-xs sm:text-sm font-bold shadow-sm transition active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
              >
                {isParsing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Extracting Plan Details...
                  </>
                ) : (
                  <>Extract Plan Details →</>
                )}
              </button>
            </div>
          </div>
        ) : (
          /* Extracted Review State */
          <div className="space-y-4 animate-fade-in">
            <div className="p-4 rounded-xl bg-white border border-stone-200 shadow-2xs">
              <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500 block mb-1">
                Extracted Summary
              </span>
              <p className="text-sm font-bold text-stone-900 leading-snug">
                {extractedData.extracted_summary}
              </p>
            </div>

            {/* Grid of Extracted Parameter Badges */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-xs">
              <div className="p-3 bg-white rounded-xl border border-stone-200">
                <span className="text-stone-400 font-bold block text-[10px] uppercase">Occasion</span>
                <span className="font-bold text-stone-900">{extractedData.event_type}</span>
              </div>
              <div className="p-3 bg-white rounded-xl border border-stone-200">
                <span className="text-stone-400 font-bold block text-[10px] uppercase">Guests</span>
                <span className="font-bold text-stone-900">{extractedData.guest_count} guests</span>
              </div>
              <div className="p-3 bg-white rounded-xl border border-stone-200">
                <span className="text-stone-400 font-bold block text-[10px] uppercase">Budget</span>
                <span className="font-bold text-stone-900">${extractedData.budget?.toLocaleString()}</span>
              </div>
              <div className="p-3 bg-white rounded-xl border border-stone-200">
                <span className="text-stone-400 font-bold block text-[10px] uppercase">Space</span>
                <span className="font-bold text-stone-900">{extractedData.venue_type || extractedData.venue || 'Flexible'}</span>
              </div>
              <div className="p-3 bg-white rounded-xl border border-stone-200">
                <span className="text-stone-400 font-bold block text-[10px] uppercase">Honoree</span>
                <span className="font-bold text-stone-900">
                  {extractedData.event_specifics?.age ? `Age ${extractedData.event_specifics.age}` : (extractedData.event_specifics?.honoreeRelation || 'Honoree')}
                </span>
              </div>
              <div className="p-3 bg-white rounded-xl border border-stone-200">
                <span className="text-stone-400 font-bold block text-[10px] uppercase">Location</span>
                <span className="font-bold text-stone-900 truncate block">
                  {extractedData.zip_code ? `${extractedData.zip_code} (${extractedData.country || 'US'})` : `${extractedData.country || 'US'}`}
                </span>
              </div>
            </div>

            {/* Theme Vibe Snippet */}
            {extractedData.theme_input && (
              <div className="p-3.5 bg-stone-100/70 rounded-xl border border-stone-200 text-xs">
                <span className="text-stone-500 font-bold block text-[10px] uppercase mb-0.5">Atmosphere & Style</span>
                <p className="font-medium text-stone-800 italic">"{extractedData.theme_input}"</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="pt-3 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-stone-200/80">
              <button
                type="button"
                onClick={() => setExtractedData(null)}
                className="text-xs font-bold text-stone-600 hover:text-stone-900 transition"
              >
                ← Edit Description
              </button>
              <div className="flex items-center gap-2.5 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={handleApply}
                  className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl border border-stone-300 bg-white hover:bg-stone-50 text-xs sm:text-sm font-bold text-stone-800 shadow-2xs transition active:scale-95"
                >
                  Review in Wizard
                </button>
                <button
                  type="button"
                  onClick={handleDirectLaunch}
                  className="flex-1 sm:flex-none px-5 py-2.5 rounded-xl bg-stone-900 hover:bg-stone-800 text-white text-xs sm:text-sm font-bold shadow-sm transition active:scale-95 flex items-center justify-center gap-1.5"
                >
                  <span>Build Themes →</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ExpressPlanModal;
